import React, { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { PetInfoEditor } from "./PetInfoEditor";
import { FormProvider, useForm, useWatch, Path } from "react-hook-form";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useStateScopedBreeds } from "@/shared/hooks/useStateScopedBreeds";
import { UsPetPoliciesSchema, UsPetPolicies, PartialUsPetPolicies } from "@/quote-ptz-us/src/schema/PtzUsQuote";
import { CaPetPoliciesSchema, CaPetPolicies, PartialCaPetPolicies } from "@/quote-ptz-ca/src/schema/PtzCaQuote";
import { useFormParentContext } from "../contexts/FormParent";
import { ResponsiveModalSheet } from "./ResponsiveModalSheet";
import { PlusCircle } from "lucide-react";
import { Badge, Button, ButtonProps } from "./ui";
import { Option, Policy, Quote } from "../types/Quote.interface";
import { cn } from "../utils";
import { FormField } from "./FormField";
import DynamicOptionsField from "./DynamicOptionsField";
import Strings from "../utils/Strings.constants";
import { PublicConfig } from "../PublicConfig";
import { PRIORITY_CODE_DEPRECATED, ErrorIdType } from "../types/SpotAPI";
import ErrorUtils from "../utils/ErrorUtils";
import { MultiPetDiscountCopy } from "../utils/MultiPetDiscountUtils";
import { useMultiPetDiscountCopy } from "../contexts/MultiPetDiscount";
import isEqual from "lodash/isEqual";
import { isAxiosError } from "axios";

export type FloatingVariant = "floating" | "floating-inner";

/** Static copy, or a resolver fed the multi-pet discount copy for the quote's rating state (see `useMultiPetDiscountCopy`). */
export type PetModalLabel = string | ((copy: MultiPetDiscountCopy) => string);

/** A `PetModalLabel` as text: resolvers get the copy for the quote's rating state, strings pass through. */
export const resolvePetModalLabel = (label: PetModalLabel | undefined, copy: MultiPetDiscountCopy): string | undefined => (typeof label === "function" ? label(copy) : label);

export type PetModalConfig = {
    resolverSchema: typeof UsPetPoliciesSchema | typeof CaPetPoliciesSchema;
    topBreeds: RegExp[];
    showMicrochip: boolean;
    formStyles: {
        wrapper: string;
        fields: Record<string, string>;
    };
    showBirthDate?: boolean;
    showAge?: boolean;
    ageField?: {
        maxAgeCat?: number;
        maxAgeDog?: number;
        maxAgeError?: string;
        constrainSelectWidthToTrigger?: boolean;
        label?: string;
        placeholder?: string;
    };
    breedField?: {
        label?: string;
        placeholder?: string;
    };
    nameField?: {
        label?: string;
    };
    badgeLabel?: PetModalLabel;
    triggerLabel?: PetModalLabel;
    hideTrigger?: boolean;
    floatingVariant?: FloatingVariant;
};

type PetModalProps = {
    initialValues: PartialUsPetPolicies | PartialCaPetPolicies;
    triggerButton?: React.ReactElement<ButtonProps>;
    isOpen: boolean;
    handleOpen?: () => void;
    onClose: (editedPetId?: string) => void;
    config: PetModalConfig;
    handleActivePetChange: (value: string) => void;
};

interface ConfirmationAlertProps {
    title: string;
    message?: string;
    confirmLabel: string;
    cancelLabel: string;
    onConfirm(): void;
    onCancel(): void;
    isLoading?: boolean;
}

type PetErrorType = Extract<ErrorIdType, "invalid-pet-name" | "invalid-pet-birthday" | "invalid-microchip">;

const AddAPetTitle = ({ badgeLabel }: { badgeLabel?: PetModalLabel }) => {
    const copy = useMultiPetDiscountCopy();
    const label = resolvePetModalLabel(badgeLabel, copy);

    return (
        <>
            <span className="text-lg font-bold text-content-primary">{Strings.ADD_A_PET}</span>
            {label && <Badge variant="secondary">{label}</Badge>}
        </>
    );
};

const PetModalForm = ({
    initialValues,
    onClose,
    config,
    handleActivePetChange,
    isEditMode,
    showRemoveConfirmation,
    setShowRemoveConfirmation,
    onUnsavedChangesVisibilityChange
}: Pick<PetModalProps, "initialValues" | "onClose" | "config" | "handleActivePetChange"> & {
    isEditMode?: boolean;
    showRemoveConfirmation: boolean;
    setShowRemoveConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
    isMultiPetLinked?: boolean;
    onUnsavedChangesVisibilityChange: (isVisible: boolean) => void;
}) => {
    const floatingVariant: "floating" | "floating-inner" = config.floatingVariant ?? "floating";
    const petOptions: Option[] = initialValues?.policies?.map(pet => ({ label: pet.name ?? "", value: pet.id ?? "" })) ?? [];
    const firstPet = initialValues?.policies?.[0];

    const [activePet, setActivePet] = useState<Policy | undefined>(firstPet);

    // State to track if we should show "unsaved changes" confirmation
    const [unsavedChangesModal, setShowUnsavedChangesModal] = useState<{
        show: boolean;
        newPetId: string;
    }>({
        show: false,
        newPetId: ""
    });

    const activePetIndex: number = useMemo(() => {
        if (!activePet) return 0;
        return initialValues?.policies?.findIndex(pet => pet.id === activePet.id) ?? 0;
    }, [activePet, initialValues?.policies]);

    const {
        appState: { underwriter, updateQuote, isQuoteUpdating, mockAPIError, asyncErrors, breedsCatalog },
        updateAppState
    } = useAppLayerContext();
    const { getValues, watch: watchParent, reset: resetParentForm } = useFormParentContext<Quote>();
    // Own instance is the fallback when this modal renders outside a controller that publishes
    // appState.breedsCatalog (e.g. add-a-pet). Scoped to the quote's rating state.
    const ownCatalog = useStateScopedBreeds({ stateCode: watchParent("ratingAddress.state") || undefined });
    const catalog = breedsCatalog ?? ownCatalog;
    const breeds = catalog.breeds;
    const totalPolicies = watchParent("policies") ?? [];
    // CA scopes breeds by province via the quote's postal code; US ignores it.
    // const { breeds } = useBreeds(underwriter, true, watchParent("ratingZipcode"));
    const petBreedsLoading = catalog.isLoading || catalog.status === "unsettled" || !breeds.length;
    const isCatalogAuthoritative = catalog.status === "authoritative" && catalog.breeds.length > 0;

    //Creates a deep copy of form data
    const normalizeFormData = (data: PartialUsPetPolicies | PartialCaPetPolicies | UsPetPolicies | CaPetPolicies | undefined): typeof data => {
        if (!data || !data.policies) return data;

        // Avoids mutating the original
        // Standardizes some data types
        return JSON.parse(JSON.stringify(data));
    };

    const form = useForm<UsPetPolicies | CaPetPolicies>({
        resolver: zodResolver(config.resolverSchema),
        defaultValues: normalizeFormData(initialValues)
    });

    const { control, handleSubmit, reset } = form;
    const policies = useWatch({ control, name: "policies" });

    // Pet error mappings for async validation errors
    const petErrorMappings: Record<PetErrorType, (errorPath: string, policyIndex: number) => { path: Path<UsPetPolicies | CaPetPolicies>; message: string } | undefined> = useMemo(
        () => ({
            "invalid-pet-name": (errorPath, policyIndex) => {
                const match = errorPath.match(/pets\[(\d+)\]/);
                if (!match) return undefined;
                return {
                    path: `policies.${policyIndex}.name` as Path<UsPetPolicies | CaPetPolicies>,
                    message: Strings.ERRORS.PET_NAME_INVALID
                };
            },
            "invalid-pet-birthday": (errorPath, policyIndex) => {
                const match = errorPath.match(/pets\[(\d+)\]/);
                if (!match) return undefined;
                return {
                    path: (underwriter === "ptz-ca" ? `policies.${policyIndex}.birthYear` : `policies.${policyIndex}.age`) as Path<UsPetPolicies | CaPetPolicies>,
                    message: Strings.ERRORS.PET_AGE_INVALID
                };
            },
            "invalid-microchip": (errorPath, policyIndex) => {
                const match = errorPath.match(/pets\[(\d+)\]/);
                if (!match) return undefined;
                return {
                    path: `policies.${policyIndex}.extra.microchipID` as Path<UsPetPolicies | CaPetPolicies>,
                    message: Strings.ERRORS.MICROCHIP_INVALID_ASYNC
                };
            }
        }),
        [underwriter]
    );

    // Handle async validation errors
    useEffect(() => {
        if (!asyncErrors?.length) return;

        asyncErrors.forEach(error => {
            if (error.id in petErrorMappings && error.at) {
                const errorMapping = petErrorMappings[error.id as PetErrorType](error.at, activePetIndex);
                if (errorMapping) {
                    form.setError(errorMapping.path, {
                        type: "manual",
                        message: error.message ?? errorMapping.message
                    });

                    // Focus on the error field
                    setTimeout(() => {
                        let elementId = errorMapping.path as string;
                        // Handle microchip field ID
                        if (elementId.includes(".extra.microchipID")) {
                            elementId = elementId.replace(".extra.microchipID", ".microchipID");
                        }
                        const element = document.getElementById(elementId);
                        if (element) {
                            element.focus();
                            element.scrollIntoView({ block: "center", behavior: "smooth" });
                        }
                    }, 100);
                }
            }
        });

        // Clear async errors after handling
        updateAppState({ asyncErrors: undefined });
    }, [asyncErrors, activePetIndex, form, petErrorMappings, updateAppState]);

    // Switch the active pet
    const switchPet = (petId: string) => {
        const pet = initialValues?.policies?.find(p => p.id === petId);
        setActivePet(pet);
    };

    //Determine if user-relevant changes exist.
    const areValuesSame = () => {
        const currentValues = form.getValues();
        const currentPet = normalizeFormData(currentValues)?.policies?.[activePetIndex];
        const initialPet = normalizeFormData(initialValues)?.policies?.[activePetIndex];

        if (!currentPet || !initialPet) return true;

        return isEqual(currentPet, initialPet);
    };

    // If user tries to switch pet while form is dirty, show a confirmation
    const updateActivePet = (petId: string) => {
        if (petId === activePet?.id) return; // no-op if same pet

        if (!areValuesSame()) {
            setShowUnsavedChangesModal({
                show: true,
                newPetId: petId
            });
            onUnsavedChangesVisibilityChange(true);
        } else {
            switchPet(petId);
        }
    };

    // Discard changes: reset form to the initial values, then switch
    const handleDiscardChanges = (petIdToSwitchTo: string) => {
        reset(); // revert to the original initialValues
        setShowUnsavedChangesModal({ show: false, newPetId: "" });
        onUnsavedChangesVisibilityChange(false);
        switchPet(petIdToSwitchTo);
    };

    // Cancel the unsaved-changes dialog (remain on current pet)
    const stayOnCurrentPet = () => {
        setShowUnsavedChangesModal({ show: false, newPetId: "" });
        onUnsavedChangesVisibilityChange(false);
    };

    const handleClose = () => {
        onClose(activePet?.id);
        reset(initialValues);
    };

    const onSubmit = async (data: UsPetPolicies | CaPetPolicies) => {
        // Check for duplicate microchips against parent form
        const currentValues = getValues();
        const existingMicrochips =
            currentValues.policies
                ?.filter(p => p.id !== data.policies[0]?.id) // Exclude current pet if editing
                ?.map(p => p.extra?.microchipID)
                ?.filter(Boolean) || [];

        const newMicrochip = data.policies[0]?.extra?.microchipID;

        if (newMicrochip && existingMicrochips.includes(newMicrochip)) {
            form.setError("policies.0.extra.microchipID", {
                type: "manual",
                message: Strings.ERRORS.MICROCHIP_DUPLICATE
            });
            return;
        }
        try {
            const currentValues = getValues();
            const updatedPolicies = currentValues.policies ? [...currentValues.policies] : [];

            data.policies.forEach(submittedPolicy => {
                const existingPolicyIndex = updatedPolicies.findIndex(currentPolicy => currentPolicy.id === submittedPolicy.id);

                if (existingPolicyIndex > -1) {
                    updatedPolicies[existingPolicyIndex] = { ...updatedPolicies[existingPolicyIndex], ...submittedPolicy };
                } else {
                    updatedPolicies.push(submittedPolicy);
                }
            });

            const updatedData = {
                ...currentValues,
                policies: updatedPolicies
            };

            if (!!updateQuote) {
                const onUpdateQuote = async () => {
                    const updateType = isEditMode ? "UPDATE" : "PET_ADDED";
                    try {
                        return await updateQuote.mutateAsync({ quote: updatedData, updateType });
                    } catch (error) {
                        const hasPcodeError = ErrorUtils.hasSpotError(error, PRIORITY_CODE_DEPRECATED);
                        if (hasPcodeError) {
                            let retryData = { ...updatedData, affiliateCode: "SPOT", discountCode: "SPOT" };
                            if (PublicConfig.ENVIRONMENT === "development" && mockAPIError) {
                                // retry with blank sim param to clear the error
                                retryData = { ...retryData, extra: { ...retryData.extra, sim: "" } };
                            }
                            const retryQuote = await updateQuote?.mutateAsync({ quote: retryData, updateType });
                            return retryQuote;
                        }
                        // Re-throw error so it can be handled by outer catch
                        throw error;
                    }
                };

                const updatedQuote = await onUpdateQuote();

                if (!!updatedQuote && !!updatedQuote.policies?.length) {
                    resetParentForm(updatedQuote);
                    const newPet = updatedQuote.policies.find(updatedPet => !currentValues.policies?.some(currentPet => currentPet.id === updatedPet.id));
                    if (newPet?.id) {
                        // Set the newly added pet as the active pet
                        setActivePet(newPet);
                        handleActivePetChange(newPet.id);
                        updateAppState({ scrollToAddedPet: true });
                    }
                }

                reset(initialValues);
                onClose(activePet?.id);
            } else {
                throw new Error("updateQuote is not defined");
            }
        } catch (error) {
            console.error("Failed to update policy:", error);

            // Extract async errors from API response and set them in app state
            if (isAxiosError(error) && error.response?.data?.error?.message) {
                const apiErrors = error.response.data.error.message;
                if (Array.isArray(apiErrors)) {
                    updateAppState({ asyncErrors: apiErrors });
                }
            } else {
                // For non-API errors, set a generic error state
                updateAppState({ hasUnknownError: true });
            }
        }
    };

    const handleRemovePet = () => {
        setShowRemoveConfirmation(true);
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>, type: "save" | "remove" | "close") => {
        if (event.key === "Enter" || event.key === " ") {
            switch (type) {
                case "save":
                    handleSubmit(onSubmit)();
                    break;
                case "remove":
                    handleRemovePet();
                    break;
                case "close":
                    handleClose();
                    break;
                default:
                    break;
            }
        }
    };

    const confirmRemovePet = async () => {
        const currentValues = getValues();
        const { policies } = currentValues;

        if (policies && policies.length > 0) {
            const petIdToRemove = activePet?.id;
            if (petIdToRemove) {
                const currentIndex = policies.findIndex(policy => policy.id === petIdToRemove);
                const updatedPolicies = policies.filter(policy => policy.id !== petIdToRemove);
                const extraData = currentValues.extra;
                const updatedIsMultiPetLinked = updatedPolicies?.length === 1 ? true : extraData?.isMultiPetLinked;

                // Determine the next active pet
                let nextActivePetId: string | undefined;
                const previousPolicy = policies[currentIndex - 1];
                if (currentIndex > 0 && !!previousPolicy) {
                    // If there's a previous pet, select it

                    nextActivePetId = previousPolicy.id;
                } else if (currentIndex === 0 && updatedPolicies.length > 0 && updatedPolicies[0]) {
                    // If it's the first pet and there are others, select the next one
                    nextActivePetId = updatedPolicies[0].id;
                }

                const updatedData = {
                    ...currentValues,
                    policies: updatedPolicies,
                    extra: { ...extraData, isMultiPetLinked: updatedIsMultiPetLinked }
                };

                const updatedQuote = await updateQuote?.mutateAsync({ quote: updatedData, updateType: "PET_REMOVED" });

                resetParentForm(updatedQuote);
                // Update the active pet
                if (nextActivePetId) {
                    handleActivePetChange(nextActivePetId);
                }
                onClose(nextActivePetId);
            }
        }
    };

    const cancelRemovePet = () => {
        setShowRemoveConfirmation(false);
    };

    const Footer = () => (
        <div className="mt-8 flex w-full flex-col gap-4">
            {isEditMode ? (
                <div className="flex w-full gap-4">
                    {totalPolicies.length > 1 && (
                        <Button variant="ghost" onClick={handleRemovePet} onKeyUp={e => handleKeyUp(e, "remove")} className="flex-1">
                            {Strings.REMOVE_PET}
                        </Button>
                    )}
                    <Button
                        loading={isQuoteUpdating}
                        variant={floatingVariant === "floating-inner" ? "default-rounded" : "default"}
                        onClick={handleSubmit(onSubmit)}
                        onKeyUp={e => handleKeyUp(e, "save")}
                        className="flex-1"
                    >
                        {Strings.SAVE}
                    </Button>
                </div>
            ) : (
                <Button
                    loading={isQuoteUpdating}
                    variant={floatingVariant === "floating-inner" ? "default-rounded" : "default"}
                    onClick={handleSubmit(onSubmit)}
                    onKeyUp={e => handleKeyUp(e, "save")}
                    className="w-full"
                    startDecorator={<PlusCircle className="size-5" />}
                >
                    {Strings.ADD_PET}
                </Button>
            )}
        </div>
    );

    const renderContent = () => {
        if (showRemoveConfirmation) {
            return (
                <div className="flex h-full flex-col items-center justify-center gap-8">
                    <h2 className="w-full text-balance text-center text-lg font-bold text-content-primary">
                        <span className="whitespace-normal">{Strings.REMOVE_PET_CONFIRMATION} </span>
                        <span className="whitespace-nowrap">
                            <span className="inline-block max-w-[200px] truncate align-bottom">{activePet?.name}</span>
                            <span>?</span>
                        </span>
                    </h2>
                    <div className="flex w-full gap-4">
                        <Button variant="ghost" onClick={cancelRemovePet} className="flex-1">
                            {Strings.CONFIRMATION_CANCEL}
                        </Button>
                        <Button variant="default" onClick={confirmRemovePet} className="flex-1" loading={isQuoteUpdating}>
                            Yes
                        </Button>
                    </div>
                </div>
            );
        }

        if (unsavedChangesModal.show) {
            return (
                <ConfirmationAlert
                    title={Strings.UNSAVED_CHANGES_TITLE}
                    message={Strings.UNSAVED_CHANGES_MESSAGE}
                    confirmLabel={Strings.UNSAVED_CHANGES_DISCARD}
                    cancelLabel={Strings.UNSAVED_CHANGES_STAY}
                    onConfirm={() => handleDiscardChanges(unsavedChangesModal.newPetId)}
                    onCancel={stayOnCurrentPet}
                />
            );
        }

        // NORMAL PET FORM UI
        return (
            <>
                {policies.length > 1 && (
                    <FormField className="mb-2">
                        <DynamicOptionsField
                            id="active-pet"
                            toggleLimit={3}
                            options={petOptions}
                            label=""
                            initialValue={activePet?.id}
                            onValueChange={value => {
                                updateActivePet(value);
                            }}
                            constrainWidthToTrigger
                            variant={floatingVariant}
                        />
                    </FormField>
                )}

                <div className="flex w-full flex-col" key={activePetIndex}>
                    <PetInfoEditor
                        petBreeds={breeds}
                        petBreedsLoading={petBreedsLoading}
                        isCatalogAuthoritative={isCatalogAuthoritative}
                        topBreeds={config.topBreeds}
                        policyIndex={activePetIndex}
                        allowDelete={false}
                        showAge={config.showAge ?? !config.showBirthDate}
                        showBirthDate={config.showBirthDate}
                        showMicrochip={config.showMicrochip}
                        ageField={config.ageField}
                        nameField={config.nameField}
                        breedField={config.breedField}
                        styles={config.formStyles}
                        breedListHeight="120px"
                        floatingVariant={floatingVariant}
                    />
                    <Footer />
                </div>
            </>
        );
    };

    return <FormProvider {...form}>{renderContent()}</FormProvider>;
};

export function PetModal({ initialValues, triggerButton, isOpen, onClose, config, handleActivePetChange }: PetModalProps) {
    const isMultiPetLinked = useAppLayerContext().appState.isMultiPetLinked;
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const [showUnsavedChangesConfirmation, setShowUnsavedChangesConfirmation] = useState(false);

    const isEditMode = initialValues?.policies?.every(policy => policy.id !== undefined);

    const getTitle = () => {
        if (showRemoveConfirmation || showUnsavedChangesConfirmation) {
            return null;
        }
        if (isEditMode && isMultiPetLinked && !!initialValues?.policies?.length && initialValues.policies.length > 1) {
            return Strings.EDIT_PETS;
        }

        return isEditMode ? Strings.EDIT_PET_DETAILS : <AddAPetTitle badgeLabel={config.badgeLabel} />;
    };

    const handleClose = (editedPetId?: string) => {
        setShowRemoveConfirmation(false);
        onClose(editedPetId);
    };

    return (
        <ResponsiveModalSheet
            isOpen={isOpen}
            onClose={handleClose}
            title={getTitle()}
            sheetSide="top"
            triggerButton={triggerButton !== undefined ? React.cloneElement(triggerButton) : undefined}
            showCloseButton={!showRemoveConfirmation && !showUnsavedChangesConfirmation}
            styles={{
                sheet: {
                    content: cn("flex flex-col", showRemoveConfirmation && "pb-16"),
                    title: "flex justify-between items-center"
                },
                modal: {
                    title: "flex justify-between items-center",
                    content: "flex flex-col !max-w-[360px]",
                    closeButton: "hidden"
                }
            }}
        >
            {isOpen && !!config && (
                <PetModalForm
                    initialValues={initialValues}
                    onClose={handleClose}
                    config={config}
                    isEditMode={isEditMode}
                    showRemoveConfirmation={showRemoveConfirmation}
                    setShowRemoveConfirmation={setShowRemoveConfirmation}
                    handleActivePetChange={handleActivePetChange}
                    isMultiPetLinked={isMultiPetLinked}
                    onUnsavedChangesVisibilityChange={setShowUnsavedChangesConfirmation}
                />
            )}
        </ResponsiveModalSheet>
    );
}

export default PetModal;

const ConfirmationAlert = (props: ConfirmationAlertProps) => {
    const { title, message, confirmLabel, cancelLabel, onConfirm, onCancel, isLoading } = props;

    return (
        <div className="flex h-full flex-col items-center justify-center gap-8">
            <h2 className="max-w-full whitespace-normal break-words text-center text-xl font-bold text-content-primary">{title}</h2>
            {message && <p className="text-center text-content-secondary">{message}</p>}
            <div className="flex w-full gap-4">
                {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                <Button autoFocus variant="ghost" onClick={onCancel} className="flex-1">
                    {cancelLabel}
                </Button>
                <Button variant="default" onClick={onConfirm} className="flex-1" loading={isLoading}>
                    {confirmLabel}
                </Button>
            </div>
        </div>
    );
};
